/**
 * How loud a thing is from here, and how much of it a wall takes away.
 *
 * Pure. No Web Audio, no three.js, no simulation. It is handed distances in
 * metres and hands back numbers between nought and one, which is the whole of
 * what this layer decides — everything else in `src/audio` is plumbing that
 * carries out a decision made here.
 *
 * ---------------------------------------------------------------------------
 * THE RADIUS IS THE POINT
 *
 * Every noise the simulation emits carries a radius, and that radius is already
 * load-bearing: it is exactly how far away somebody can hear you do it, and a
 * write-up is what happens when they do. The player has been playing a game
 * about being heard with no way at all to tell how far a sound of theirs
 * reached.
 *
 * So the audible envelope is not a general-purpose attenuation curve that
 * happens to sound nice. It is *the same radius*, made audible: full at the
 * source, falling away, and reaching nothing at exactly the distance the
 * simulation stops carrying it. A player who learns by ear that a ridden cart
 * is silent from the far end of the corridor has learned something true about
 * the rules, and has learned it the way you learn things about a building.
 * ---------------------------------------------------------------------------
 */

/**
 * What the simulation leaves at the very edge of a sense.
 *
 * `perception.js` fades a witness's confidence to this rather than to nothing,
 * so a noise at the rim of somebody's hearing is a third of a noise and not a
 * silence. The same line is used here, for the same reason: a sound should be
 * clearly falling away for the whole of its reach rather than fading to
 * inaudible in the first few feet and then technically continuing.
 */
export const EDGE = 0.35;

/**
 * How loud a noise of this radius is, this far from it. 0..1.
 *
 * The simulation's own line, closed at the rim. The line alone ends at `EDGE`
 * and then stops, which is right for a confidence — a witness either heard it
 * or did not — and wrong for a sound: a cart rolling steadily away would cut
 * out at a third of its volume, and the click is audible from the other end of
 * the building. Multiplying by a window that closes at the same distance keeps
 * the shape, keeps the edge, and lands on nothing where the rules do.
 *
 * Scale-free on purpose. Two sounds at the same fraction of their own radius
 * are the same loudness, which is what makes radii comparable by ear: half way
 * to the edge of a big noise and half way to the edge of a small one sound
 * alike, so the difference you hear between them is distance and not kind.
 */
export function carry(distance, radius) {
  if (!(radius > 0)) return 0;
  const t = Math.max(0, distance) / radius;
  if (t >= 1) return 0;
  return (1 - (1 - EDGE) * t) * (1 - t * t);
}

/**
 * How far this body can hear, as a multiple of what a sober one can.
 *
 * Acuity is already a number in the simulation — the pen and the joint both
 * extend `impairment.earshot`, which is how the "you heard something to the
 * north" line reaches further when you are on something. This turns that same
 * ratio into reach.
 *
 * It stretches the *radius*, which is the difference between the two things it
 * could have done. Making everything louder would be a volume knob with a
 * drug attached. Making the radius longer means things that were beyond
 * hearing become hearable — the corridor gets deeper rather than nearer, which
 * is what being unusually aware of a room actually feels like, and is the
 * version that tells you something you did not already know.
 */
export function reach(radius, acuity = 1) {
  return radius * Math.max(1, acuity || 1);
}

/**
 * What a wall does to a sound.
 *
 * Muffled rather than absent, which is the entire reason this matters. Sight
 * is blocked by the building and hearing is not, and concealment trades one
 * for the other: under a table nobody can see you smash a bottle and everybody
 * can hear it. A layer that simply silenced anything out of line of sight
 * would be quietly telling the player the opposite of the rule they are
 * playing against.
 *
 * Low frequencies go through walls and high ones do not, which is why this is a
 * corner frequency and a small loss rather than just a loss: through a wall a
 * cart is a rumble with no castors in it, and the castors are the part that
 * tells you how fast it is going.
 */
export function muffle(blocked) {
  return blocked ? { hz: 380, gain: 0.42 } : { hz: 18000, gain: 1 };
}

/**
 * Everything at once: what this noise is worth from where the listener stands.
 *
 * Returns the gain, the corner frequency, and — for anything that wants to know
 * rather than hear — whether it was reachable at all and how far through its
 * own radius the listener is standing. The last of those is what a check can
 * use to prove the envelope really is the simulation's radius and not a curve
 * that happens to be quiet at the right moment.
 */
export function mixFor({ distance, radius, acuity = 1, blocked = false }) {
  const reached = reach(radius, acuity);
  const wall = muffle(blocked);
  const level = carry(distance, reached);
  return {
    gain: level * wall.gain,
    hz: wall.hz,
    /** Before the wall, so a check can separate distance from occlusion. */
    open: level,
    reach: reached,
    blocked: Boolean(blocked),
    audible: level > 0,
    /** 0 at the source, 1 at the rim of what this listener can hear. */
    through: reached > 0 ? Math.min(1, Math.max(0, distance) / reached) : 1,
  };
}

/**
 * Where a sound is, relative to which way the listener is facing.
 *
 * Handed back as a unit vector in the listener's own frame — right, up, and
 * ahead — because that is what a panner wants and because it is the one piece
 * of this that is easy to get subtly wrong and hard to hear as wrong. A body
 * faces `(sin yaw, cos yaw)` in this project, which is the convention the rest
 * of the world is built on.
 */
export function bearing({ from, to, yaw }) {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const dy = (to.y ?? 0) - (from.y ?? 0);
  const flat = Math.hypot(dx, dz);
  if (flat < 1e-6) return { right: 0, up: dy === 0 ? 0 : Math.sign(dy), ahead: 1, distance: Math.abs(dy) };

  const sin = Math.sin(yaw);
  const cos = Math.cos(yaw);
  // Ahead is the facing vector; right is that turned a quarter turn clockwise.
  const ahead = (dx * sin + dz * cos) / flat;
  const right = (dx * cos - dz * sin) / flat;
  return { right, up: 0, ahead, distance: Math.hypot(dx, dz, dy) };
}
